import { useEffect } from 'react';
import { Menu, MenuItemLink, usePermissions, useSidebarState, useTranslate } from 'react-admin';
import { Button, useTheme, useMediaQuery } from '@mui/material';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import LocationIcon from '@/assets/icons/location-icon.svg?react';
import TransactionsIcon from '@/assets/icons/transactions-icon.svg?react';
import TransactionsReviewIcon from '@/assets/icons/transaction-review-icon.svg?react';
import CashOutIcon from '@/assets/icons/cash-out-icon.svg?react';
import ContainersIcon from '@/assets/icons/containers-icon.svg?react';
import ProductsIcon from '@/assets/icons/products-icon.svg?react';
import ProductsBasisIcon from '@/assets/icons/products-basis-icon.svg?react';
import TagsIcon from '@/assets/icons/tags-icon.svg?react';
import PosIcon from '@/assets/icons/pos-icon.svg?react';
import DevicesIcon from '@/assets/icons/devices-icon.svg?react';
import AnalyticsIcon from '@/assets/icons/analytics-icon.svg?react';
import MarcetingIcon from '@/assets/icons/marketing-icon.svg?react';
import MarcetingPromoIcon from '@/assets/icons/marketing-promo-icon.svg?react';
import MarcetingCampaineIcon from '@/assets/icons/marketing-campaine-icon.svg?react';
import ContractorsIcon from '@/assets/icons/contractors-icon.svg?react';
import ContractorsReviewIcon from '@/assets/icons/contractors-review-icon.svg?react';
import UsersIcon from '@/assets/icons/users-icon.svg?react';
import ServisIcon from '@/assets/icons/servis-icon.svg?react';
import ProblemsReviewIcon from '@/assets/icons/problems-review-icon.svg?react';
import ServisTasksIcon from '@/assets/icons/servis-tasks-icon.svg?react';
import DailyEventIcon from '@/assets/icons/daily-event-icon.svg?react';
import DevicesGroupIcon from '@/assets/icons/devices-group-icon.svg?react';
import DevicesNewIcon from '@/assets/icons/devices-new-icon.svg?react';
import ExpandMoreIcon from '@/assets/icons/arrow-down-icon.svg?react';
import ExpandLessIcon from '@/assets/icons/arrow-up-icon.svg?react';
import BillOfMaterialsIcon from '@/assets/icons/bill-of-materials-icon.svg?react';

const CustomSubMenu = ({ label, children, icon, isOpen, onToggle }) => {
    const translate = useTranslate();
    const translatedLabel = translate(label);

    return (
        <div className='menu-item'>
            <div
                onClick={onToggle}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') onToggle();
                }}
                className='menu-primary'
                role='button'
                tabIndex='0'
            >
                {icon}
                <span>{translatedLabel}</span>
                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            {isOpen && <div className='menu-secondary'>{children}</div>}
        </div>
    );
};

export const CustomMenu = ({ closeSidebar, openMenus, setOpenMenus, ...props }) => {
    const { permissions } = usePermissions();
    const theme = useTheme();
    const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
    const [open, setOpen] = useSidebarState();
    const translate = useTranslate();

    const handleMenuToggle = menuKey => {
        setOpenMenus(prev => ({
            ...prev,
            [menuKey]: !prev[menuKey],
        }));
    };

    const toggleSidebar = () => setOpen(!open);

    useEffect(() => {
        if (isLgDown && !open) {
            setOpen(true);
        }
    }, [isLgDown, open, setOpen]);

    if (permissions) {
        return (
            <div>
                <Menu
                    sx={{
                        width: 300,
                    }}
                    {...props}
                >
                    <CustomSubMenu
                        label='menu.transactions'
                        icon={<TransactionsIcon />}
                        isOpen={openMenus.transactions}
                        onToggle={() => handleMenuToggle('transactions')}
                    >
                        <MenuItemLink
                            to='/transaction'
                            primaryText={translate('menu.transactionsOverview')}
                            leftIcon={<TransactionsReviewIcon />}
                            onClick={closeSidebar}
                        />
                        {(permissions?.isAdmin || permissions?.isService) && (
                            <MenuItemLink
                                to='/test-transaction'
                                primaryText={translate('menu.testTransactionsOverview')}
                                leftIcon={<TransactionsReviewIcon />}
                                onClick={closeSidebar}
                            />
                        )}
                        <MenuItemLink
                            to='#'
                            primaryText={translate('menu.depositWithdrawals')}
                            leftIcon={<CashOutIcon />}
                            onClick={closeSidebar}
                            disabled={true}
                        />
                        <MenuItemLink to='/bag' primaryText={translate('menu.bags')} leftIcon={<ContainersIcon />} onClick={closeSidebar} />
                    </CustomSubMenu>
                    <CustomSubMenu label='menu.locations' icon={<LocationIcon />} isOpen={openMenus.locations} onToggle={() => handleMenuToggle('locations')}>
                        {permissions.isAdmin || permissions.isClient || permissions.isBok || permissions.isService ? (
                            <MenuItemLink
                                to='/collection-point'
                                primaryText={translate('menu.collectionPoint')}
                                leftIcon={<PosIcon />}
                                onClick={closeSidebar}
                            />
                        ) : null}
                        {!permissions.isService && (
                            <MenuItemLink to='/device' primaryText={translate('menu.devices')} leftIcon={<DevicesIcon />} onClick={closeSidebar} />
                        )}
                        {permissions.isAdmin ||
                        permissions.isClient ||
                        permissions.isClientMaster ||
                        permissions.isPosOwner ||
                        permissions.isBok ||
                        permissions.isService ? (
                            <MenuItemLink
                                to='/collection-point-map'
                                primaryText={translate('menu.collectionPointMap')}
                                leftIcon={<PosIcon />}
                                onClick={closeSidebar}
                            />
                        ) : null}
                        {permissions.isAdmin || permissions.isBok ? (
                            <MenuItemLink
                                to='/device-group'
                                primaryText={translate('menu.deviceGroups')}
                                leftIcon={<DevicesGroupIcon />}
                                onClick={closeSidebar}
                            />
                        ) : null}
                        {permissions.isAdmin || permissions.isBok ? (
                            <MenuItemLink to='/new-device' primaryText={translate('menu.newDevices')} leftIcon={<DevicesNewIcon />} onClick={closeSidebar} />
                        ) : null}
                    </CustomSubMenu>
                    {!permissions.isPosOwner && (permissions.isClient || permissions.isBok || permissions.isAdmin) && (
                        <CustomSubMenu label='menu.products' icon={<ProductsIcon />} isOpen={openMenus.products} onToggle={() => handleMenuToggle('products')}>
                            <MenuItemLink to='/product' primaryText={translate('menu.productBase')} leftIcon={<ProductsBasisIcon />} onClick={closeSidebar} />
                            <MenuItemLink to='/tag-category' primaryText={translate('menu.tagManagement')} leftIcon={<TagsIcon />} onClick={closeSidebar} />
                            <MenuItemLink to='/material' primaryText={translate('menu.materials')} leftIcon={<ProductsBasisIcon />} onClick={closeSidebar} />
                        </CustomSubMenu>
                    )}
                    {(permissions.isAdmin || permissions.isBok || permissions.isClient || permissions.isServiceMaster) && (
                        <CustomSubMenu
                            label='menu.analytics'
                            icon={<AnalyticsIcon />}
                            isOpen={openMenus.analytics}
                            onToggle={() => handleMenuToggle('analytics')}
                        >
                            {(permissions.isAdmin || permissions.isBok || permissions.isClient || permissions.isServiceMaster) && (
                                <MenuItemLink
                                    to='/analytic-transactions'
                                    primaryText={translate('menu.transactionAnalytics')}
                                    leftIcon={<ProductsBasisIcon />}
                                    onClick={closeSidebar}
                                />
                            )}
                            {(permissions.isAdmin || permissions.isBok || permissions.isClient) && (
                                <MenuItemLink
                                    to='/analytic-products'
                                    primaryText={translate('menu.productAnalytics')}
                                    leftIcon={<ProductsBasisIcon />}
                                    onClick={closeSidebar}
                                />
                            )}
                            {(permissions.isAdmin || permissions.isBok || permissions.isClient || permissions.isServiceMaster) && (
                                <MenuItemLink
                                    to='/analytic-service'
                                    primaryText={translate('menu.deviceUptimeAnalytics')}
                                    leftIcon={<ProductsBasisIcon />}
                                    onClick={closeSidebar}
                                />
                            )}
                        </CustomSubMenu>
                    )}
                    <CustomSubMenu label='menu.marketing' icon={<MarcetingIcon />} isOpen={openMenus.marketing} onToggle={() => handleMenuToggle('marketing')}>
                        <MenuItemLink
                            to='/promotional-campaigns'
                            primaryText={translate('menu.promotionalCampaigns')}
                            leftIcon={<MarcetingPromoIcon />}
                            onClick={closeSidebar}
                        />
                        {(permissions?.isAdmin || permissions?.isBok || permissions?.isClient || permissions?.isClientMaster) && !permissions?.isPosOwner ? (
                            <MenuItemLink to='/ad' primaryText={translate('menu.adsCampaigns')} leftIcon={<MarcetingCampaineIcon />} onClick={closeSidebar} />
                        ) : null}
                    </CustomSubMenu>
                    {(!permissions.isClient || permissions.isClientMaster) && (
                        <CustomSubMenu
                            label='menu.contractors'
                            icon={<ContractorsIcon />}
                            isOpen={openMenus.contractors}
                            onToggle={() => handleMenuToggle('contractors')}
                        >
                            {permissions.isAdmin || permissions.isBok ? (
                                <MenuItemLink
                                    to='/retail-chain'
                                    primaryText={translate('menu.retailChains')}
                                    leftIcon={<ContractorsReviewIcon />}
                                    onClick={closeSidebar}
                                />
                            ) : null}
                            {permissions.isAdmin || permissions.isBok ? (
                                <MenuItemLink to='/brand' primaryText={translate('menu.brands')} leftIcon={<ContractorsReviewIcon />} onClick={closeSidebar} />
                            ) : null}
                            {permissions.isAdmin || permissions.isBok ? (
                                <MenuItemLink
                                    to='/contractor'
                                    primaryText={translate('menu.contractorsOverview')}
                                    leftIcon={<ContractorsReviewIcon />}
                                    onClick={closeSidebar}
                                />
                            ) : null}
                            {permissions.isAdmin || permissions.isClientMaster || permissions.isServiceMaster || permissions.isBok ? (
                                <MenuItemLink to='/user' primaryText={translate('menu.users')} leftIcon={<UsersIcon />} onClick={closeSidebar} />
                            ) : null}
                        </CustomSubMenu>
                    )}
                    <CustomSubMenu label='menu.service' icon={<ServisIcon />} isOpen={openMenus.service} onToggle={() => handleMenuToggle('service')}>
                        {permissions?.isAdmin || permissions?.isService || permissions?.isBok ? (
                            <>
                                <MenuItemLink
                                    to='/device-issue'
                                    primaryText={translate('menu.problemsOverview')}
                                    leftIcon={<ProblemsReviewIcon />}
                                    onClick={closeSidebar}
                                />
                                <MenuItemLink
                                    to='/service-request'
                                    primaryText={translate('menu.serviceRequests')}
                                    leftIcon={<ServisTasksIcon />}
                                    onClick={closeSidebar}
                                />
                                <MenuItemLink
                                    to='/service-inspection'
                                    primaryText={translate('menu.serviceInspections')}
                                    leftIcon={<ServisTasksIcon />}
                                    onClick={closeSidebar}
                                />
                            </>
                        ) : null}
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <MenuItemLink
                                to='/spare-part'
                                primaryText={translate('menu.billOfMaterials')}
                                leftIcon={<BillOfMaterialsIcon />}
                                onClick={closeSidebar}
                            />
                        ) : null}
                        <MenuItemLink to='#' primaryText={translate('menu.eventLog')} leftIcon={<DailyEventIcon />} onClick={closeSidebar} disabled={true} />
                        {permissions?.isAdmin || permissions?.isBok ? (
                            <MenuItemLink
                                to='/service-company'
                                primaryText={translate('menu.serviceCompanies')}
                                leftIcon={<ServisTasksIcon />}
                                onClick={closeSidebar}
                            />
                        ) : null}
                    </CustomSubMenu>
                    {permissions?.isAdmin || permissions?.isBok ? (
                        <CustomSubMenu label='menu.settings' icon={<ServisIcon />} isOpen={openMenus.settings} onToggle={() => handleMenuToggle('settings')}>
                            <MenuItemLink to='/software' primaryText={translate('menu.software')} leftIcon={<ServisTasksIcon />} onClick={closeSidebar} />
                            <MenuItemLink
                                to='/software-alias'
                                primaryText={translate('menu.softwareAlias')}
                                leftIcon={<ServisTasksIcon />}
                                onClick={closeSidebar}
                            />
                            <MenuItemLink
                                to='/software-update-rule'
                                primaryText={translate('menu.softwareUpdateRule')}
                                leftIcon={<ServisTasksIcon />}
                                onClick={closeSidebar}
                            />
                            <MenuItemLink
                                to='/device-error'
                                primaryText={translate('menu.errorManagement')}
                                leftIcon={<ServisTasksIcon />}
                                onClick={closeSidebar}
                            />
                            {permissions?.isAdmin ? (
                                <MenuItemLink
                                    to='/prompt'
                                    primaryText={translate('menu.promptManagement')}
                                    leftIcon={<ServisTasksIcon />}
                                    onClick={closeSidebar}
                                />
                            ) : null}
                            {permissions?.isAdmin ? (
                                <MenuItemLink
                                    to='/access-key'
                                    primaryText={translate('menu.accessKey')}
                                    leftIcon={<ServisTasksIcon />}
                                    onClick={closeSidebar}
                                />
                            ) : null}
                            <MenuItemLink
                                to='/device-model'
                                primaryText={translate('menu.deviceModelManagement')}
                                leftIcon={<ServisTasksIcon />}
                                onClick={closeSidebar}
                            />
                            <MenuItemLink
                                to='/parameter'
                                primaryText={translate('menu.parameterManagement')}
                                leftIcon={<ServisTasksIcon />}
                                onClick={closeSidebar}
                            />
                        </CustomSubMenu>
                    ) : null}
                </Menu>
                {!isLgDown && (
                    <Button onClick={toggleSidebar} color='secondary' size='small' variant='contained' fullWidth className='toggle-menu-button'>
                        <ArrowLeftIcon />
                    </Button>
                )}
            </div>
        );
    }

    return null;
};
